import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/PaymentSuccess.css';
import { processPayment } from '../utils/api'; // API 함수 import

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState({
    companyCode: '',
    plan: '',
    amount: 0,
    planName: '',
    fullPlanName: '',
    paymentMethod: '신용카드'
  });
  
  // 결제 처리 상태를 추적하는 ref 추가
  const paymentProcessed = useRef(false);
  
  // 플랜 타입 -> 이름 매핑
  const planTypeToName = {
    'basic': '1개월',
    'premium': '6개월',
    'enterprise': '12개월'
  };
  
  // 플랜 별 가격 정보
  const planPriceMapping = {
    '1개월': 199000,
    '6개월': 1074000,
    '12개월': 1788000
  };
  
  // 전체 플랜 이름 생성 함수
  const getFullPlanName = (shortName) => {
    return `TapGo Plus ${shortName} 플랜`;
  };
  
  useEffect(() => {
    // URL에서 searchParams 가져오기
    const searchParams = new URLSearchParams(location.search);
    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');
    
    // 토스페이먼츠 결제 승인 처리 및 백엔드 API 호출
    const completePayment = async () => {
      // 이미 처리된 결제면 중복 실행 방지
      if (paymentProcessed.current) return;
      
      if (!paymentKey || !orderId || !amount) {
        setError('결제 정보가 올바르지 않습니다.');
        setIsLoading(false);
        return;
      }
      
      try {
        // 중복 실행 방지를 위해 플래그 설정
        paymentProcessed.current = true;
        
        // 주문 ID에서 플랜 정보 추출
        const planType = getPlanTypeFromOrderId(orderId);
        const planName = planTypeToName[planType] || '구독';
        const fullPlanName = getFullPlanName(planName);
        
        // 백엔드 API 호출 - 토스페이먼츠 결제 처리
        // 백엔드에서 토스페이먼츠 API 호출 및 검증, 카드 정보 추출 등을 처리
        const result = await processPayment(planType, {
          paymentKey,
          orderId,
          amount: parseInt(amount),
          planName: fullPlanName
        });
        
        console.log('결제 성공 응답 전체:', JSON.stringify(result, null, 2));
        
        // 응답에서 회사 코드 로그 확인
        console.log('회사 코드:', result.data.company_code);
        
        // 결제 데이터 설정
        setPaymentData({
          companyCode: result.data.company_code,
          plan: result.data.plan,
          amount: parseInt(amount),
          planName: planName,
          fullPlanName: result.data.plan_name,
          paymentMethod: result.data.payment_method || '신용카드' // 백엔드에서 제공하는 결제 방법 정보
        });
        
        // 회사 코드를 localStorage에 저장
        if (result.data.company_code) {
          // 기존 userInfo 가져오기
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          
          // 회사 코드 추가
          userInfo.middleCompanyCode = result.data.company_code;
          
          // 업데이트된 정보 저장
          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          console.log('회사 코드를 localStorage에 저장했습니다:', result.data.company_code);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('결제 처리 오류:', error);
        setError('결제 처리 중 오류가 발생했습니다: ' + error.message);
        setIsLoading(false);
      }
    };
    
    completePayment();
  }, [location]);
  
  // 주문 ID에서 플랜 타입 추출
  const getPlanTypeFromOrderId = (orderId) => {
    try {
      // 주문 ID 형식: "tapgo-timestamp-random-planType"
      const parts = orderId.split('-');
      if (parts.length >= 4) {
        // 마지막 부분이 플랜 타입
        return parts[3];
      }
      
      // 기본값 반환
      return 'basic';
    } catch (error) {
      console.error('주문 ID 파싱 오류:', error);
      return 'basic';
    }
  };
  
  // 금액 포맷팅 함수
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };
  
  // 플랜 기간 계산
  const getPlanPeriod = (planName) => {
    const today = new Date();
    const endDate = new Date(today);
    
    switch(planName) {
      case '1개월':
        endDate.setMonth(today.getMonth() + 1);
        break;
      case '6개월':
        endDate.setMonth(today.getMonth() + 6);
        break;
      case '12개월':
        endDate.setMonth(today.getMonth() + 12);
        break;
      default:
        endDate.setMonth(today.getMonth() + 1);
    }
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return `${formatDate(today)} ~ ${formatDate(endDate)}`;
  };
  
  // 관리자 페이지로 이동
  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="payment-success-container">
        <div className="success-content">
          <h2>결제 정보를 처리 중입니다...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-container">
        <div className="success-content">
          <h2>오류가 발생했습니다</h2>
          <p className="error-message">{error}</p>
          <button className="admin-page-button" onClick={() => navigate('/plan-upgrade')}>
            다시 시도하기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      {/* 녹색 라인 */}
      <div className="green-line"></div>
      
      <div className="success-content">
        {/* 체크 아이콘 */}
        <div className="check-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
          </svg>
        </div>
        
        {/* 성공 메시지 */}
        <h1 className="success-message">{paymentData.fullPlanName || getFullPlanName(paymentData.planName)}을</h1>
        <h1 className="success-message">구독해주셔서 감사합니다!</h1>
        
        {/* 결제 금액 */}
        <p className="payment-amount">결제 금액: <span className="amount">{formatCurrency(paymentData.amount)}</span></p>
        
        {/* 구독 기간 */}
        <p className="plan-info">구독 기간: <span className="plan-period">{getPlanPeriod(paymentData.planName)}</span></p>
        
        {/* 회사 코드 */}
        <p className="company-code-message">발급된 회사코드는 <span className="company-code">{paymentData.companyCode}</span> 입니다.</p>
        
        {/* 관리자 페이지로 이동 버튼 */}
        <button className="admin-page-button" onClick={handleGoToDashboard}>
          관리자 페이지로 이동
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess; 